/**
 * EvaluationViewerModal - Read-only viewer for a submitted QA evaluation.
 *
 * 2026-07-16 v2: Fixed. First version had two bugs:
 *   (1) rendered `a.value` directly, which is a dict {answer: "yes"|"no"|"na"|number|text}
 *       - showed as "[object Object]"
 *   (2) missing question text - AnswerOut only has question_id, so we
 *       have to JOIN against the form's sections/questions on the client.
 *   (3) header grid had cells overlapping on narrow widths.
 *
 * v2 fetches BOTH the evaluation and the form, then walks the form's
 * sections -> questions and joins answers by question_id. Renders per
 * question kind (yes_no / scale_1_5 / multi_choice / free_text).
 *
 * Also adds:
 *   - a Download PDF button (calls /api/qa/evaluations/{id}/download)
 *   - cleaner header layout (single row of clearly-separated pills)
 *   - full agent + call context so it's obvious which call this is
 */
import React, { useEffect, useState } from 'react';
import Modal, { Badge } from '../../ui/Modal';
import { qaApi } from '../../../api/client';

function fmtDate(iso) {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function fmtDuration(sec) {
  if (sec == null) return '-';
  const s = Math.floor(Number(sec) || 0);
  const m = Math.floor(s / 60);
  return `${m}:${String(s % 60).padStart(2, '0')}`;
}

// From/To derivation:
//   inbound:  From = caller_id (external caller), To = did OR agent_extension
//   outbound: From = agent_extension (agent dialing), To = caller_id (destination)
//   internal: best effort, whatever data exists
function deriveFrom(rec) {
  if (!rec) return '';
  if (rec.direction === 'outbound') {
    return rec.agent_extension || rec.agent_name || '';
  }
  return rec.caller_id || '';
}
function deriveTo(rec) {
  if (!rec) return '';
  if (rec.direction === 'outbound') {
    return rec.caller_id || rec.did || '';
  }
  // Inbound / other: prefer DID (dialed number) then agent_extension.
  return rec.did || rec.agent_extension || '';
}

// A single label:value row inside the header blocks. Uses a fixed-width
// label column so nothing overlaps regardless of value length.
function HeaderRow({ label, children }) {
  return (
    <div className="flex items-baseline gap-3">
      <div className="w-28 shrink-0 text-xs text-surface-muted uppercase tracking-wide">
        {label}
      </div>
      <div className="flex-1 min-w-0 flex items-center gap-1 flex-wrap">
        {children}
      </div>
    </div>
  );
}

function scoreTone(score, passed) {
  if (passed === false) return 'red';
  if (score == null) return 'neutral';
  if (score >= 90) return 'green';
  if (score >= 75) return 'blue';
  if (score >= 60) return 'amber';
  return 'red';
}

function statusTone(status) {
  return {
    draft:      'neutral',
    submitted:  'blue',
    approved:   'green',
    disputed:   'amber',
    rejected:   'red',
  }[status] || 'neutral';
}

// Render one answer given the question metadata + answer row.
// Returns a React element (badges/text depending on question kind).
function AnswerCell({ q, a }) {
  if (!a) return <span className="italic text-surface-muted text-xs">(no answer)</span>;
  const raw = a.value && typeof a.value === 'object' ? a.value.answer : null;

  if (q.kind === 'yes_no') {
    if (raw === 'yes') return <Badge tone="green">YES</Badge>;
    if (raw === 'no')  return <Badge tone="red">NO</Badge>;
    if (raw === 'na')  return <Badge tone="neutral">N/A</Badge>;
    return <span className="text-xs">{String(raw ?? '-')}</span>;
  }
  if (q.kind === 'scale_1_5') {
    const n = Number(raw);
    const tone = n >= 4 ? 'green' : n === 3 ? 'blue' : n === 2 ? 'amber' : 'red';
    return <Badge tone={tone}>{Number.isFinite(n) ? `${n} / 5` : String(raw ?? '-')}</Badge>;
  }
  if (q.kind === 'multi_choice') {
    // Resolve label from choices
    let label = String(raw ?? '-');
    if (Array.isArray(q.choices)) {
      const hit = q.choices.find(c => String(c?.value) === String(raw));
      if (hit) label = hit.label || hit.value || label;
    }
    return <Badge tone="blue">{label}</Badge>;
  }
  if (q.kind === 'free_text') {
    return (
      <div className="text-xs whitespace-pre-wrap text-surface-text dark:text-surface-dark-text">
        {String(raw ?? '') || <span className="italic text-surface-muted">(empty)</span>}
      </div>
    );
  }
  return <span className="text-xs">{String(raw ?? '-')}</span>;
}

export default function EvaluationViewerModal({
  open,
  onClose,
  recordingId,
  recordingLabel,
  recording,  // 2026-07-16 v3: optional recording object for header enrichment
              // (call_id, direction, caller_id, did, agent_extension, etc.).
              // If not passed the header still works with just recordingLabel.
}) {
  const [state, setState] = useState('loading');   // loading | ready | error | empty
  const [evals, setEvals] = useState([]);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [form, setForm] = useState(null);          // FormOut
  const [formError, setFormError] = useState(null);
  const [error, setError] = useState(null);
  const [enrichedRecording, setEnrichedRecording] = useState(null); // Fetched if not passed

  // 2026-07-16 v3 bug fix: previously we had two useEffects keyed on
  // [open,recordingId] then [selected?.form_id]. Since both same-modal
  // reopens produce the same form_id, the second effect never re-fired
  // and `form` stayed at null forever (spinner). Merged into one effect
  // keyed only on `open` so every open triggers a fresh load.
  useEffect(() => {
    if (!open || !recordingId) return;
    let cancelled = false;
    setState('loading');
    setError(null);
    setForm(null);
    setFormError(null);
    setEnrichedRecording(null);

    (async () => {
      try {
        // 1) List evaluations for this recording
        const rows = await qaApi.listForRecording(recordingId);
        if (cancelled) return;
        const visible = (rows || []).filter(e => e.status && e.status !== 'draft');
        if (!visible.length) {
          setEvals([]);
          setState('empty');
          return;
        }
        setEvals(visible);
        setSelectedIdx(0);

        // 2) In parallel: fetch the form for the FIRST eval + enrich the
        //    recording if the caller didn't pass one.
        const firstFormId = visible[0].form_id;
        const tasks = [];
        if (firstFormId) {
          tasks.push(
            qaApi.getForm(firstFormId)
              .then(f => { if (!cancelled) setForm(f); })
              .catch(err => { if (!cancelled) setFormError(err); }),
          );
        }
        if (!recording) {
          tasks.push(
            // We rely on the recordings API to fetch the recording meta.
            // Import lazily to avoid a circular; the modal is already
            // reached via `import { recordingsApi } from '../../../api/client'`
            // in the parent, but we need it here for the enrichment path.
            import('../../../api/client').then(({ recordingsApi }) =>
              recordingsApi.get(recordingId)
                .then(rec => { if (!cancelled) setEnrichedRecording(rec); })
                .catch(() => { /* non-fatal */ })
            ),
          );
        }
        await Promise.allSettled(tasks);
        if (!cancelled) setState('ready');
      } catch (err) {
        if (cancelled) return;
        setError(err);
        setState('error');
      }
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, recordingId]);

  const selected = evals[selectedIdx] || null;

  // Refetch the form when the user picks a DIFFERENT evaluation (rare).
  // Guarded so it does not re-fetch on the same form_id — that already
  // happens on `open` above.
  useEffect(() => {
    if (!open) return;
    if (!selected?.form_id) return;
    // If our current `form` already matches, skip.
    if (form && form.id === selected.form_id) return;
    let cancelled = false;
    setForm(null);
    setFormError(null);
    (async () => {
      try {
        const f = await qaApi.getForm(selected.form_id);
        if (!cancelled) setForm(f);
      } catch (err) {
        if (!cancelled) setFormError(err);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIdx, open]);

  // Recording used for the header — prefer the caller-provided one,
  // fall back to what we just enriched.
  const rec = recording || enrichedRecording;

  // Build a Map<question_id, AnswerOut> for O(1) join
  const answersByQid = React.useMemo(() => {
    const m = new Map();
    for (const a of (selected?.answers || [])) {
      if (a?.question_id) m.set(String(a.question_id), a);
    }
    return m;
  }, [selected]);

  // PDF download uses browser fetch + blob to preserve the auth
  // header from our `api()` helper. Plain <a download> would bypass
  // the auth wrapper.
  const downloadPdf = async () => {
    if (!selected?.id) return;
    try {
      const resp = await fetch(`/api/qa/evaluations/${selected.id}/download`, {
        credentials: 'include',
      });
      if (!resp.ok) {
        alert(`Download failed: HTTP ${resp.status}`);
        return;
      }
      const blob = await resp.blob();
      const disp = resp.headers.get('Content-Disposition') || '';
      let filename = `evaluation-${String(selected.id).slice(0, 8)}.pdf`;
      const m = /filename="([^"]+)"/.exec(disp);
      if (m) filename = m[1];
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 3000);
    } catch (err) {
      alert(`Download failed: ${err?.message || err}`);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="xl"
      title={recordingLabel ? `Evaluation - ${recordingLabel}` : 'Evaluation'}
      footer={
        state === 'ready' && selected ? (
          <>
            <button
              onClick={downloadPdf}
              className="px-3 py-1.5 rounded-thin text-sm font-medium bg-continuum-500 hover:bg-continuum-600 text-white"
              title="Download this evaluation as a PDF"
            >
              ↓ Download PDF
            </button>
            <button
              onClick={onClose}
              className="px-3 py-1.5 rounded-thin text-sm font-medium border border-surface-border dark:border-surface-dark-border hover:bg-surface-subtle dark:hover:bg-surface-dark-subtle"
            >
              Close
            </button>
          </>
        ) : null
      }
    >
      {state === 'loading' && (
        <div className="text-sm text-surface-muted flex items-center gap-2">
          <span className="inline-block w-3 h-3 border-2 border-surface-muted border-t-transparent rounded-full animate-spin" />
          Loading evaluation...
        </div>
      )}
      {state === 'error' && (
        <div className="text-sm text-red-600">
          Failed to load evaluation: {error?.message || String(error)}
        </div>
      )}
      {state === 'empty' && (
        <div className="text-sm text-surface-muted">
          No submitted evaluation found for this recording.
        </div>
      )}

      {state === 'ready' && selected && (
        <div className="space-y-5">
          {/* Multi-eval selector (rare) */}
          {evals.length > 1 && (
            <div className="flex items-center gap-2">
              <label className="text-xs text-surface-muted">Evaluation:</label>
              <select
                value={selectedIdx}
                onChange={(e) => setSelectedIdx(Number(e.target.value))}
                className="text-sm rounded-thin border border-surface-border dark:border-surface-dark-border px-2 py-1 bg-transparent"
              >
                {evals.map((e, i) => (
                  <option key={e.id} value={i}>
                    {e.form_name || 'Untitled form'} - {fmtDate(e.submitted_at || e.created_at)}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Header — 3-block layout with visual separators so nothing overlaps.
              Block 1: score + status pills (top).
              Block 2: Call context (direction, from, to, agent, ext, call time, duration).
              Block 3: Evaluation context (form, evaluator, submitted, coached, edited).
              Each field is on its own line inside its block — no more grid overlaps. */}
          <div className="rounded-thin border border-surface-border dark:border-surface-dark-border bg-surface-subtle dark:bg-surface-dark-subtle divide-y divide-surface-border dark:divide-surface-dark-border">

            {/* Block 1: score + status pills */}
            <div className="p-4">
              <div className="flex items-center gap-4 flex-wrap">
                <div className="flex items-baseline gap-2">
                  <div className="text-3xl font-bold leading-none">
                    {selected.overall_score != null
                      ? `${Number(selected.overall_score).toFixed(1)}`
                      : '-'}
                  </div>
                  <div className="text-xs text-surface-muted">/ 100</div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge tone={scoreTone(selected.overall_score, selected.passed)}>
                    {selected.passed === false ? 'FAILED'
                      : selected.passed === true ? 'PASSED'
                      : (selected.status || 'n/a').toUpperCase()}
                  </Badge>
                  <Badge tone={statusTone(selected.status)}>{selected.status}</Badge>
                  {selected.auto_fail_triggered && <Badge tone="red">Auto-fail triggered</Badge>}
                  {selected.coached_at && <Badge tone="purple">Coached</Badge>}
                </div>
              </div>
            </div>

            {/* Block 2: Call context. Direction pill + From/To/Agent/Ext/Time/Duration. */}
            {rec && (
              <div className="p-4">
                <div className="text-[11px] uppercase tracking-wider text-surface-muted mb-2 font-semibold">
                  Call
                </div>
                <div className="space-y-1.5 text-sm">
                  <HeaderRow label="Direction">
                    <Badge tone={rec.direction === 'inbound' ? 'blue' : rec.direction === 'outbound' ? 'green' : 'neutral'}>
                      {(rec.direction || 'unknown').toUpperCase()}
                    </Badge>
                  </HeaderRow>
                  <HeaderRow label="From">
                    <span className="font-mono">{deriveFrom(rec) || '-'}</span>
                  </HeaderRow>
                  <HeaderRow label="To">
                    <span className="font-mono">{deriveTo(rec) || '-'}</span>
                  </HeaderRow>
                  <HeaderRow label="Agent">
                    <span>{rec.agent_name || '-'}</span>
                    {rec.agent_extension && (
                      <span className="ml-2 text-xs text-surface-muted font-mono">ext {rec.agent_extension}</span>
                    )}
                  </HeaderRow>
                  {(rec.queue_name || rec.queue_id) && (
                    <HeaderRow label="Queue">
                      <span>{rec.queue_name || '-'}</span>
                      {rec.queue_id && rec.queue_name && rec.queue_name !== rec.queue_id && (
                        <span className="ml-2 text-xs text-surface-muted font-mono">({rec.queue_id})</span>
                      )}
                      {rec.queue_id && !rec.queue_name && (
                        <span className="font-mono">{rec.queue_id}</span>
                      )}
                    </HeaderRow>
                  )}
                  <HeaderRow label="Call started">
                    <span>{fmtDate(rec.started_at)}</span>
                  </HeaderRow>
                  <HeaderRow label="Duration">
                    <span>{fmtDuration(rec.duration_sec)}</span>
                  </HeaderRow>
                  {rec.call_id && (
                    <HeaderRow label="Call ID">
                      <span className="font-mono text-xs">{rec.call_id}</span>
                    </HeaderRow>
                  )}
                </div>
              </div>
            )}

            {/* Block 3: Evaluation metadata. */}
            <div className="p-4">
              <div className="text-[11px] uppercase tracking-wider text-surface-muted mb-2 font-semibold">
                Evaluation
              </div>
              <div className="space-y-1.5 text-sm">
                <HeaderRow label="Form">
                  <span className="font-medium">{selected.form_name || '-'}</span>
                </HeaderRow>
                <HeaderRow label="Evaluator">
                  <span className="font-medium">{selected.evaluator_name || '-'}</span>
                </HeaderRow>
                <HeaderRow label="Submitted">
                  <span>{fmtDate(selected.submitted_at)}</span>
                </HeaderRow>
                <HeaderRow label="Created">
                  <span className="text-xs text-surface-muted">{fmtDate(selected.created_at)}</span>
                </HeaderRow>
                {selected.coached_at && (
                  <HeaderRow label="Coached">
                    <span>{fmtDate(selected.coached_at)}</span>
                    {selected.coached_by_name && (
                      <span className="ml-2 text-xs text-surface-muted">by {selected.coached_by_name}</span>
                    )}
                  </HeaderRow>
                )}
                {selected.edited_at && (
                  <HeaderRow label="Edited">
                    <span>{fmtDate(selected.edited_at)}</span>
                    {selected.edited_by_name && (
                      <span className="ml-2 text-xs text-surface-muted">by {selected.edited_by_name}</span>
                    )}
                  </HeaderRow>
                )}
              </div>
            </div>
          </div>

          {/* Evaluator notes */}
          {selected.evaluator_notes && (
            <div>
              <div className="text-xs text-surface-muted mb-1 font-medium">Evaluator notes</div>
              <div className="text-sm whitespace-pre-wrap rounded-thin border border-surface-border dark:border-surface-dark-border p-3">
                {selected.evaluator_notes}
              </div>
            </div>
          )}

          {/* Coaching notes */}
          {selected.coached_notes && (
            <div>
              <div className="text-xs text-surface-muted mb-1 font-medium">Coaching notes</div>
              <div className="text-sm whitespace-pre-wrap rounded-thin border border-purple-300 dark:border-purple-800 p-3 bg-purple-50 dark:bg-purple-900/20">
                {selected.coached_notes}
              </div>
            </div>
          )}

          {/* Sections + Questions + Answers */}
          <div>
            <div className="text-xs text-surface-muted mb-2 font-medium">Questions &amp; answers</div>

            {formError && (
              <div className="text-xs text-red-600 mb-2">
                Could not load form structure: {formError?.message || String(formError)}
              </div>
            )}
            {!form && !formError && (
              <div className="text-xs text-surface-muted flex items-center gap-2">
                <span className="inline-block w-3 h-3 border-2 border-surface-muted border-t-transparent rounded-full animate-spin" />
                Loading form structure...
              </div>
            )}

            {form && (form.sections || []).length === 0 && (
              <div className="text-xs text-surface-muted italic">(no sections on this form)</div>
            )}

            {form && (form.sections || []).map(section => (
              <div key={section.id} className="mt-3">
                <div className="text-sm font-semibold mb-1">{section.title}</div>
                {section.description && (
                  <div className="text-xs text-surface-muted mb-2">{section.description}</div>
                )}
                {/* 2026-07-16 v4 wrap fix: previous version relied on `break-words`
                    alone, but if any ancestor allowed horizontal scroll (Modal body
                    has overflow-auto) the container could grow to fit the longest
                    unbroken sentence. Now:
                      - Explicit `w-full` on the question card
                      - `min-w-0` on inner flex containers (so flex children can shrink)
                      - `whitespace-normal break-words` on the text spans
                      - `overflow-hidden` on the outer card as a hard belt-and-suspenders
                        so nothing can force the parent wider than its allotted width. */}
                <div className="space-y-2">
                  {(section.questions || []).map((q, qi) => {
                    const a = answersByQid.get(String(q.id));
                    const scoreStr = a && a.score != null
                      ? `${Number(a.score).toFixed(1)}`
                      : '-';
                    return (
                      <div
                        key={q.id}
                        className="w-full overflow-hidden rounded-thin border border-surface-border dark:border-surface-dark-border p-3"
                      >
                        {/* Row 1 — question text spans full width and wraps. */}
                        <p className="text-sm whitespace-normal break-words leading-snug m-0">
                          <span className="text-surface-muted text-xs mr-1 font-mono">Q{qi + 1}.</span>
                          <span>{q.text}</span>
                          {q.auto_fail && (
                            <span className="ml-2 text-[10px] font-semibold text-amber-600 dark:text-amber-400 whitespace-nowrap">
                              (auto-fail)
                            </span>
                          )}
                        </p>
                        {/* Row 2 — answer + score, wraps as flex-wrap. */}
                        <div className="mt-2 flex items-center gap-3 flex-wrap min-w-0">
                          <AnswerCell q={q} a={a} />
                          <span className="text-xs text-surface-muted whitespace-nowrap ml-auto">
                            score: <span className="font-medium">{scoreStr}</span>
                          </span>
                        </div>
                        {a?.comment && (
                          <p className="mt-2 text-xs text-surface-muted border-l-2 border-surface-border dark:border-surface-dark-border pl-2 whitespace-pre-wrap break-words leading-snug m-0">
                            {a.comment}
                          </p>
                        )}
                      </div>
                    );
                  })}
                  {(section.questions || []).length === 0 && (
                    <div className="text-xs text-surface-muted italic">(no questions in this section)</div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Agent notes (if any) */}
          {selected.agent_notes && (
            <div>
              <div className="text-xs text-surface-muted mb-1 font-medium">Agent response</div>
              <div className="text-sm whitespace-pre-wrap rounded-thin border border-surface-border dark:border-surface-dark-border p-3">
                {selected.agent_notes}
              </div>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
